/**
 * Layer-0 bot brain, B2 (WORKBOARD §14): *what* to do — utility job selection. Pure.
 *
 * Instead of a fixed mode chain, every job is scored from what the bot honestly knows and the
 * best one wins (with inertia, so a bot does not flicker):
 *
 *   engage   — fight the target from a scored spot (B1); worth less when hurt or outnumbered
 *   take     — walk onto a point the team does not hold (shooting on the way); nearer is worth more
 *   hold     — stand in a held point that is under threat, at its least exposed spot
 *   retreat  — break contact to a hidden spot further off; grows as health falls and threats grow
 *   search   — go where an enemy was last seen or heard, while it is fresh
 *   roam     — nothing better to do
 *   escort   — a squad order (lane D)
 *
 * Every weight is in JobWeights (data — B4 evolves it). Squad orders boost the job they name.
 */

import { dpow, dexp } from '../core/dmath';

export type ActionJob = 'engage' | 'take' | 'hold' | 'retreat' | 'search' | 'roam' | 'escort';
export const ACTION_JOBS: ActionJob[] = ['engage', 'take', 'hold', 'retreat', 'search', 'roam', 'escort'];

export interface JobWeights {
  engage: number;
  /** Take a point; × takeUnderFire when a target is in sight (push the point through a fight, or not). */
  take: number;
  takeUnderFire: number;
  /** Hold a held point that is threatened (an enemy capturing it, or seen near it). */
  hold: number;
  /** Retreat, scaled by (1 − health)^retreatCurve and by being outnumbered. */
  retreat: number;
  retreatCurve: number;
  /** How much being outnumbered (threats seen beyond allies near) cuts engage and feeds retreat. */
  outnumbered: number;
  search: number;
  /** Seconds over which a sighting goes stale for search. */
  searchFade: number;
  roam: number;
  escort: number;
  /** A squad order's job is worth this much more. */
  orderBoost: number;
  /** The current job keeps this bonus (no flicker). */
  inertia: number;
  /** Person-lengths over which a point's pull halves (distance decay for take / hold). */
  objectiveRange: number;
}

export const DEFAULT_JOB_WEIGHTS: JobWeights = {
  engage: 1.0, take: 0.9, takeUnderFire: 0.6, hold: 1.1, retreat: 1.6, retreatCurve: 2, outnumbered: 0.5,
  search: 0.5, searchFade: 12, roam: 0.1, escort: 0.8, orderBoost: 0.6, inertia: 0.15, objectiveRange: 60,
};

/** What the bot knows this moment, reduced to the numbers the jobs read. Distances in person-lengths. */
export interface JobContext {
  hasTarget: boolean;
  /** 0–1 */
  health: number;
  /** Enemies in sight. */
  threats: number;
  /** Living teammates within a short range (person-lengths, the caller's choice). */
  alliesNear: number;
  /** Distance to the nearest point worth taking, or null. */
  takeDist: number | null;
  /** Distance to the nearest held point under threat, or null. */
  holdDist: number | null;
  /** Seconds since an enemy was last seen or heard, or null. */
  lastKnownAge: number | null;
  /** A squad order, if any. */
  order: 'take' | 'hold' | 'escort' | null;
  current: ActionJob | null;
}

export function jobScores(c: JobContext, w: JobWeights = DEFAULT_JOB_WEIGHTS): Record<ActionJob, number> {
  const outnumber = Math.max(0, c.threats - c.alliesNear - 1);
  const pull = (d: number | null) => d === null ? 0 : 1 / (1 + d / w.objectiveRange);
  const s: Record<ActionJob, number> = {
    engage: c.hasTarget ? w.engage * (0.4 + 0.6 * c.health) * Math.max(0, 1 - w.outnumbered * outnumber / 3) : 0,
    take: c.takeDist !== null ? w.take * pull(c.takeDist) * (c.hasTarget ? w.takeUnderFire : 1) : 0,
    hold: c.holdDist !== null ? w.hold * pull(c.holdDist) : 0,
    retreat: c.hasTarget ? w.retreat * dpow(1 - c.health, w.retreatCurve) * (1 + w.outnumbered * outnumber) : 0,
    search: !c.hasTarget && c.lastKnownAge !== null ? w.search * dexp(-c.lastKnownAge / w.searchFade) : 0,
    roam: w.roam,
    escort: c.order === 'escort' ? w.escort : 0,
  };
  if (c.order === 'take') s.take *= 1 + w.orderBoost;
  if (c.order === 'hold') s.hold *= 1 + w.orderBoost;
  if (c.current) s[c.current] += w.inertia;
  return s;
}

/** The best job (ties broken by ACTION_JOBS order — deterministic). */
export function chooseJob(c: JobContext, w: JobWeights = DEFAULT_JOB_WEIGHTS): ActionJob {
  const s = jobScores(c, w);
  let best: ActionJob = 'roam';
  for (const j of ACTION_JOBS) if (s[j] > s[best]) best = j;
  return best;
}

// =============================================================================
// B3 roles (owner, Oct 9: "a utility bot that plays the objective but performs optimally with
// assault / defence escort") — weight profiles on the same brain
// =============================================================================

export type ActionRole = 'objective' | 'assault' | 'defence';
/** Roles by unit order within a team: objective, assault, defence, repeating. */
export const ROLE_ORDER: ActionRole[] = ['objective', 'assault', 'defence'];

export const ROLE_WEIGHTS: Record<ActionRole, JobWeights> = {
  /** Takes and holds points; fights when it must. */
  objective: { ...DEFAULT_JOB_WEIGHTS, take: 1.3, takeUnderFire: 0.8, hold: 1.4, engage: 0.8, escort: 0 },
  /** Leads its objective player toward the point and fights whatever appears. */
  assault: { ...DEFAULT_JOB_WEIGHTS, engage: 1.3, take: 0.4, takeUnderFire: 0.3, hold: 0.4, escort: 1.0, outnumbered: 0.3, retreat: 1.0 },
  /** Covers its objective player: overwatch round the point, trails it on the move. */
  defence: { ...DEFAULT_JOB_WEIGHTS, engage: 1.0, take: 0.3, takeUnderFire: 0.3, hold: 0.9, escort: 1.1, retreat: 1.4 },
};

/** A unit's role from its order within its team (`index` = its rank by id among teammates). */
export function roleForIndex(index: number): ActionRole {
  return ROLE_ORDER[((index % ROLE_ORDER.length) + ROLE_ORDER.length) % ROLE_ORDER.length];
}

